import type { Map as MapLibreMap } from "maplibre-gl";
import type { Feature } from "geojson";

import { AppDispatch, RootState } from "@/store";
import {
  resetAnalysisSummaryState,
  setAnalysisSummaryState
} from "@/store/slice/analysisSummary";
import { resetDatasetState, setDatasetState } from "@/store/slice/datasetSlice";
import {
  resetGlobalFilter,
  setGlobalFilterState
} from "@/store/slice/globalFilterSlice";
import { resetMapState, setMapState } from "@/store/slice/mapSlice";
import type {
  MapCamera,
  OnboardingSnapshot
} from "@/store/slice/onboardingSlice";
import {
  resetSpatialState,
  setSpatialState,
  type SpatialState
} from "@/store/slice/spatialSlice";
import { resetUIDataConfig, setUIDataConfig } from "@/store/slice/uiSlice";
import { resetWidgetState, setWidgetState } from "@/store/slice/widgetSlice";
import type { SpatialAggregationType } from "@/types/common";
import type { Dataset, DatasetData, DatasetsMap } from "@/types/dataset";
import type { GetOnboardingModulesResponse } from "@/types/onboarding";
import { getAvailableDatasets } from "@/utils/dataset";

/**
 * Resolve the BE module id for a module code, or null when the code is not in
 * the BE module list.
 */
export function resolveModuleId(
  modulesResp: GetOnboardingModulesResponse,
  moduleCode: string
): string | null {
  return modulesResp.modules.find((m) => m.code === moduleCode)?.id ?? null;
}

/**
 * Capture the live MapLibre camera so exitOnboardingMode can fly the user back to the
 * exact view they left. Returns null when no map is mounted (e.g. in tests or
 * before the map loads), which restoreMapCamera treats as a no-op.
 */
export function captureMapCamera(map: MapLibreMap | null): MapCamera | null {
  if (!map) return null;

  const { lng, lat } = map.getCenter();
  return {
    center: [lng, lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch()
  };
}

/**
 * Restore a previously captured camera onto the live map instance. No-op when
 * either the map or the camera is absent (e.g. a snapshot persisted before the
 * camera field existed, or a fresh map after a page refresh).
 */
export function restoreMapCamera(
  map: MapLibreMap | null,
  camera: MapCamera | null
): void {
  if (!map || !camera) return;

  map.jumpTo({
    center: camera.center,
    zoom: camera.zoom,
    bearing: camera.bearing,
    pitch: camera.pitch
  });
}

const addUniqueFeature = (
  featuresByKey: Map<string, Feature>,
  key: string,
  feature: Feature | undefined
): void => {
  if (!feature?.geometry) return;

  const featureKey =
    feature.id === undefined || feature.id === null ? key : String(feature.id);

  featuresByKey.set(featureKey, feature);
};

export function getSnapshotDrawnPolygonFeatures(
  snapshot: OnboardingSnapshot
): Feature[] {
  const spatial = snapshot.spatial as Partial<SpatialState> | null | undefined;
  const featuresByKey = new Map<string, Feature>();

  Object.entries(spatial?.polygonSettings ?? {}).forEach(([key, value]) => {
    addUniqueFeature(featuresByKey, key, value?.polygon as Feature | undefined);
  });

  Object.entries(spatial?.polygonSettingsTemp ?? {}).forEach(([key, value]) => {
    addUniqueFeature(featuresByKey, key, value?.polygon as Feature | undefined);
  });

  Object.entries(spatial?.drawnPolygon ?? {}).forEach(([key, feature]) => {
    addUniqueFeature(featuresByKey, key, feature as Feature | undefined);
  });

  return Array.from(featuresByKey.values());
}

/**
 * Re-render drawn polygons onto the mapbox-gl-draw control after exitOnboardingMode
 * restores the spatial slice. The enter flow cleared the control, and a Redux
 * restore alone doesn't redraw it. Pass the snapshot's features directly so
 * restore is not coupled to a same-tick selector refresh, and so temp/custom
 * draw polygons come back with the saved analysis polygons.
 */
export function restoreDrawnPolygons(
  deleteAllFeatures: () => void,
  renderDrawPolygon: (features?: Feature[]) => void,
  features?: Feature[]
): void {
  // Clear any onboarding-drawn features first, then redraw the user's polygons.
  deleteAllFeatures();
  renderDrawPolygon(features);
}

/**
 * Snapshot every slice the onboarding can mutate so exitOnboardingMode can restore the
 * user's real state on skip/complete. analysisJob is intentionally excluded —
 * it tracks real BE jobs that shouldn't be reset on exit. The live map camera
 * rides along so the imperative map view is restored together with the slices,
 * and entryPath records the route to navigate back to on exit.
 */
export function buildOnboardingSnapshot(
  state: RootState,
  mapCamera: MapCamera | null = null,
  entryPath: string | null = null
): OnboardingSnapshot {
  return {
    dataset: state.dataset,
    map: state.map,
    ui: state.ui,
    spatial: {
      ...state.spatial,
      currRunningAnalysisId: "",
      analysisResultOpenSource: ""
    },
    analysisSummary: state.analysisSummary,
    globalFilter: state.globalFilter,
    widget: state.widget,
    mapCamera,
    entryPath
  };
}

/**
 * Reset every snapshotted slice to a clean baseline so the onboarding starts fresh.
 * auth + analysisJob are left untouched (analysisJob tracks real BE jobs).
 */
export function resetOnboardingSlices(dispatch: AppDispatch): void {
  dispatch(resetDatasetState());
  dispatch(resetMapState());
  dispatch(resetUIDataConfig());
  dispatch(resetSpatialState());
  dispatch(resetAnalysisSummaryState());
  dispatch(resetGlobalFilter());
  dispatch(resetWidgetState());
}

/**
 * Wipe every dataset source + layer the user had rendered before entering
 * onboarding. resetOnboardingSlices clears the Redux dataset slice, but MapLibre
 * sources/layers are imperative — they outlive a Redux reset, so the user's real
 * datasets would stay painted on top of the clean onboarding canvas. Mirrors the
 * per-dataset removal in useMapLayers.handleResetDatasetToHistoryDataset, but
 * removes every available dataset rather than only the post-history additions.
 */
export function resetOnboardingMapLayers(
  datasets: DatasetsMap,
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void
): void {
  Object.entries(getAvailableDatasets(datasets)).forEach(
    ([datasetId, dataset]) => {
      handleRemoveSourceLayer(datasetId, dataset.properties.spatialType);
    }
  );
}

/**
 * Repaint the dataset sources + layers the user had rendered before entering
 * onboarding — the symmetric counterpart to resetOnboardingMapLayers. restoreSnapshot
 * puts the dataset slice back in Redux, but MapLibre sources/layers are imperative:
 * a Redux restore alone won't repaint them, so the user's real datasets would stay
 * missing from the map. Repaint from the snapshot, then reapply their stacking order.
 *
 * The snapshot's dataset slice is stored as `unknown`, so it's cast to the fields
 * the layer handlers need. Onboarding-painted layers are still cleared when the
 * snapshot predates the dataset field.
 */
export function restoreOnboardingMapLayers(
  snapshotDataset: unknown,
  currentDatasets: DatasetsMap,
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void,
  handleResetLayerData: (
    datasets: { [id: string]: Dataset },
    rawDatasets: { [id: string]: DatasetData }
  ) => void,
  handleResetLayerOrder: (layerOrders: string[]) => void
): void {
  const dataset = snapshotDataset as
    | {
        datasets: DatasetsMap;
        rawDatasets: { [id: string]: DatasetData };
        visualizationOrder: string[];
      }
    | null
    | undefined;

  // Remove datasets painted during onboarding (for example the explore-dataset
  // fixture) before repainting the user's saved datasets from the snapshot.
  resetOnboardingMapLayers(currentDatasets, handleRemoveSourceLayer);

  if (!dataset) return;

  // Repaint before reordering: handleResetLayerOrder moves layers already present
  // in the style, so the layers must be added back first or there's nothing to move.
  handleResetLayerData(dataset.datasets, dataset.rawDatasets);
  handleResetLayerOrder(dataset.visualizationOrder ?? []);
}

/**
 * Push every snapshotted slice back into Redux on onboarding exit, restoring the
 * user's real pre-onboarding state. Mirrors resetOnboardingSlices: same slice set, same
 * order, and analysisJob stays untouched. Snapshot fields are stored as
 * `unknown`, so each is cast to its action's payload type at dispatch.
 */
export function restoreSnapshot(
  dispatch: AppDispatch,
  snapshot: OnboardingSnapshot
): void {
  dispatch(
    setDatasetState(snapshot.dataset as Parameters<typeof setDatasetState>[0])
  );
  dispatch(setMapState(snapshot.map as Parameters<typeof setMapState>[0]));
  dispatch(
    setUIDataConfig(snapshot.ui as Parameters<typeof setUIDataConfig>[0])
  );
  dispatch(
    setSpatialState(snapshot.spatial as Parameters<typeof setSpatialState>[0])
  );
  dispatch(
    setAnalysisSummaryState(
      snapshot.analysisSummary as Parameters<typeof setAnalysisSummaryState>[0]
    )
  );
  dispatch(
    setGlobalFilterState(
      snapshot.globalFilter as Parameters<typeof setGlobalFilterState>[0]
    )
  );
  dispatch(
    setWidgetState(snapshot.widget as Parameters<typeof setWidgetState>[0])
  );
}
